import {
  money,
  fromMinorUnit,
  firstNonBlank,
  toShortName,
  classifyBreakdownRow,
} from '@shared/lib';
import { t } from '@shared/i18n';
import type {
  ApiExpenseDto,
  ExpenseDto,
  PendingExpenseSaveResponse,
  TransactionDto,
  TransactionUserDto,
} from './schemas';
import type {
  DepositConversion,
  Expense,
  ExpenseConversion,
  ExpenseId,
  ExpenseLocation,
  ExpenseRecurrence,
  LedgerEntry,
  NewExpense,
  SaveBillInput,
  SavedBill,
} from '../model/types';

/** Resolve a participant's display name + avatar from a transaction's `users` list. */
function makeResolver(users: readonly TransactionUserDto[]) {
  const byId = new Map(users.map((u) => [u.user_id, u]));
  return {
    name: (id: string): string => {
      const u = byId.get(id);
      // A guest member has no account of their own — the wire row can echo the OWNER account's
      // profile name, so show the "Guest" label instead of that misleading name (mobile parity).
      if (u?.is_guest) return t.common.guest;
      // An invited-by-phone participant has no display_name/full_name yet — fall back to their
      // phone number rather than a blank "Paid by"/"Deposited by"/settlement label. `firstNonBlank`,
      // not `??`: some rows send '' or a whitespace-only name rather than null/undefined. Shorten a
      // real name to "First L" (mobile's `shortName` convention) — a phone number has no space, so
      // it passes through toShortName unchanged.
      return toShortName(firstNonBlank(u?.display_name, u?.full_name, u?.phone_number));
    },
    avatar: (id: string): string | null => byId.get(id)?.profile_picture_url ?? null,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/** Best-effort recurrence parse — the backend rule shape varies, so read known keys defensively. */
function toRecurrence(raw: unknown): ExpenseRecurrence | null {
  const r = asRecord(raw);
  if (!r) return null;
  const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  const next =
    str(r.next_date) ?? str(r.next_occurrence) ?? str(r.next_run_at) ?? str(r.next_run_date);
  const frequency = str(r.frequency) ?? str(r.type) ?? str(r.unit);
  const start = str(r.start_date);
  const end = str(r.end_date);
  // Only a recognizable rule is worth surfacing.
  if (!frequency && next === null && start === null) return null;
  return {
    frequency,
    interval: num(r.interval),
    day: num(r.day) ?? num(r.day_of_month),
    nextDate: next ? new Date(next) : null,
    startDate: start ? new Date(start) : null,
    endType: str(r.end_type),
    endDate: end ? new Date(end) : null,
    reminderDaysBefore: num(r.reminder_days_before),
  };
}

/** Parse the DTO's flat `location*` fields into `ExpenseLocation` — null when no location was saved. */
function toApiLocation(dto: ApiExpenseDto): ExpenseLocation | null {
  const lat = typeof dto.location_lat === 'string' ? Number(dto.location_lat) : dto.location_lat;
  const long =
    typeof dto.location_long === 'string' ? Number(dto.location_long) : dto.location_long;
  if (!lat || !long || !Number.isFinite(lat) || !Number.isFinite(long)) return null;
  const formattedAddress = dto.location ?? '';
  const address1 = dto.location_address1 ?? '';
  // Prefer the formatted address's first segment (matches entities/location's own name derivation
  // for a place with no separate POI name) — the API doesn't send a distinct place-name field.
  // A bare house/plot number (e.g. "732") or a Plus Code ("2HHH+Q38") as first segment is
  // meaningless alone on the location chip: a number prefers the full street line (else widens to
  // two segments); a Plus Code is skipped for the first real segment after it (same rules as
  // entities/location's `nameFromAddress`).
  const plusCodeRe = /^[23456789CFGHJMPQRVWX]{2,8}\+[23456789CFGHJMPQRVWX]{2,}$/i;
  const segments = formattedAddress.split(',').map((s) => s.trim());
  const firstSegment = segments[0] ?? '';
  let name = firstSegment !== '' ? firstSegment : address1;
  if (plusCodeRe.test(firstSegment)) {
    name =
      segments.find((s) => s !== '' && !plusCodeRe.test(s) && !/^\d+$/.test(s)) ?? firstSegment;
  } else if (/^\d+$/.test(firstSegment)) {
    name =
      address1 && address1 !== firstSegment
        ? address1
        : segments[1]
        ? `${firstSegment}, ${segments[1]}`
        : firstSegment;
  }
  return {
    name,
    formattedAddress,
    address1,
    address2: dto.location_address2 ?? '',
    city: dto.location_city ?? '',
    state: dto.location_state ?? '',
    zip: dto.location_zip ?? '',
    country: dto.location_country ?? '',
    lat,
    long,
  };
}

/** Pull a usable URL out of an attachment value (string, or `{ url | path | file_url }`). */
function toAttachmentUrl(raw: unknown): string | null {
  if (typeof raw === 'string') return raw || null;
  const o = asRecord(raw);
  if (!o) return null;
  const url = o.url ?? o.path ?? o.file_url;
  return typeof url === 'string' && url ? url : null;
}

export function toExpense(dto: ExpenseDto): Expense {
  return {
    id: dto.id as ExpenseId,
    groupId: dto.group_id,
    description: dto.description,
    amount: money(dto.amount_minor),
    currency: dto.currency,
    paidByUserId: dto.paid_by.id,
    paidByName: toShortName(dto.paid_by.name),
    date: new Date(dto.date),
    splits: dto.splits.map((split) => ({
      userId: split.user_id,
      userName: toShortName(split.user_name),
      avatarUrl: null,
      shareMinor: money(split.share_minor),
    })),
  };
}

/**
 * Anti-corruption: real backend expense DTO → domain `Expense`. Amounts arrive in MINOR units
 * (same convention as group balances — brand directly, do NOT scale). Participant names + avatars
 * are resolved from the transaction's `users` (pass them in); without `users` names default to ''.
 */
export function toApiExpense(
  dto: ApiExpenseDto,
  users: readonly TransactionUserDto[] = [],
): Expense {
  const resolve = makeResolver(users);
  const payerId = dto.payments.find((p) => p.user_id)?.user_id ?? dto.user_id ?? '';
  const symbol = dto.group_currency?.symbol;
  const conversion: ExpenseConversion | null = dto.is_currency_converted
    ? {
        originalCurrency: dto.expense_currency?.currency ?? '',
        originalSymbol: dto.expense_currency?.symbol ?? '',
        originalAmount: money(Math.round(dto.expense_amount)),
        targetCurrency: dto.group_currency?.currency ?? '',
        rate: dto.exchange_rate ?? null,
        date: dto.exchange_rate_date ? new Date(dto.exchange_rate_date) : null,
      }
    : null;
  return {
    id: dto._id as ExpenseId,
    groupId: dto.group_id,
    description: dto.description,
    amount: money(Math.round(dto.group_currency_amount)),
    currency: dto.group_currency?.currency ?? '',
    // Only set when present so consumers can `?? ` to a symbol derived from the currency code.
    ...(symbol ? { currencySymbol: symbol } : {}),
    paidByUserId: payerId,
    paidByName: resolve.name(payerId),
    date: new Date(dto.date),
    isPrivate: dto.is_private,
    ...(dto.split_type ? { splitType: dto.split_type } : {}),
    categoryName: dto.expense_type?.name ?? '',
    categoryImage: dto.expense_type?.image ?? null,
    categoryColor: dto.expense_type?.color ?? null,
    note: dto.notes ?? '',
    createdAt: dto.created_at ? new Date(dto.created_at) : null,
    createdByUserId: dto.user_id ?? '',
    createdByName: resolve.name(dto.user_id ?? ''),
    payments: dto.payments
      .filter((p) => !!p.user_id || p.type === 'wallet')
      .map((p) => ({
        // A WALLET leg is keyed by the wallet ACCOUNT id from `wallet_id` (its `user_id` is
        // null) — matching the create payload's `paid_by[i][id]` and the update payload's
        // `paid_by_wallet_id`; losing it here made edit-mode re-saves fail the "WALLET payment
        // requires paid_by_wallet_id" validation.
        userId: (p.type === 'wallet' ? p.wallet_id : p.user_id) ?? p.user_id ?? '',
        userName: resolve.name(p.user_id ?? ''),
        avatarUrl: resolve.avatar(p.user_id ?? ''),
        amount: money(Math.round(p.group_paid_amount)),
        billAmount: money(Math.round(p.paid_amount)),
        type: p.type === 'wallet' ? ('wallet' as const) : ('user' as const),
      })),
    conversion,
    recurrence: toRecurrence(dto.recurring_rules),
    attachmentUrl: toAttachmentUrl(dto.attachment),
    location: toApiLocation(dto),
    splits: dto.splits.map((split) => ({
      userId: split.user_id,
      userName: resolve.name(split.user_id),
      avatarUrl: resolve.avatar(split.user_id),
      shareMinor: money(Math.round(split.group_expense_amount)),
      billShareMinor: money(Math.round(split.expense_amount)),
      splitEnabled: split.split_enabled,
      value: split.value,
    })),
    ...(dto.items.length > 0
      ? {
          breakdown: dto.items.map((item) => ({
            name: item.name,
            amountMinor: money(Math.round(item.amount)),
            sign: item.sing === '-' ? ('-' as const) : ('+' as const),
            // The wire sends EVERY row as type "Item" (see `toPendingExpenseFields`), so a saved
            // Tip/Discount/Tax/Other is told apart by its name / "-" sign — `classifyBreakdownRow`.
            type: classifyBreakdownRow({ type: item.type, name: item.name, sign: item.sing }),
          })),
        }
      : {}),
  };
}

/**
 * Map one EXPENSE journal-entry transaction → domain `Expense`, resolving participant names +
 * avatars from the transaction's own `users`. Returns null for non-expense rows (settlement /
 * deposit / wallet-balance closure) — those need their own mappers once the API shapes are known.
 */
export function toTransactionExpense(tx: TransactionDto): Expense | null {
  if (tx.reference_type !== 'EXPENSE' || !tx.expense) return null;
  return toApiExpense(tx.expense, tx.users);
}

/**
 * Map one journal-entry transaction → a `LedgerEntry`, dispatching on `reference_type`. Participant
 * names are resolved from the transaction's own `users`. Returns null for unknown/empty rows.
 */
export function toLedgerEntry(tx: TransactionDto): LedgerEntry | null {
  const resolve = makeResolver(tx.users);
  const date = tx.date ? new Date(tx.date) : tx.created_at ? new Date(tx.created_at) : new Date(0);

  switch (tx.reference_type) {
    case 'EXPENSE':
      return tx.expense
        ? { kind: 'expense', date, expense: toApiExpense(tx.expense, tx.users) }
        : null;

    case 'SETTLEMENT': {
      const s = tx.settle;
      if (!s) return null;
      return {
        kind: 'settlement',
        date,
        settlement: {
          id: s._id ?? tx.record_id ?? '',
          fromId: s.paid_from ?? '',
          fromName: resolve.name(s.paid_from ?? ''),
          fromProfileImage: resolve.avatar(s.paid_from ?? ''),
          toId: s.paid_to ?? '',
          toName: resolve.name(s.paid_to ?? ''),
          toProfileImage: resolve.avatar(s.paid_to ?? ''),
          amount: money(Math.round(s.amount)),
          currencySymbol: s.currency_symbol ?? null,
          date: s.date ? new Date(s.date) : date,
          notes: s.notes ?? null,
          createdByName: s.created_by_user_id ? resolve.name(s.created_by_user_id) : null,
          createdAt: s.created_at ? new Date(s.created_at) : null,
        },
      };
    }

    case 'WALLET_TOPUP': {
      const dep = tx.deposit;
      if (!dep) return null;
      const isConverted =
        (typeof dep.exchange_rate === 'number' ||
          typeof dep.selected_currency_amount === 'number') &&
        dep.selected_currency_id !== undefined &&
        dep.selected_currency_id !== null &&
        (dep.currency_id ? dep.selected_currency_id !== dep.currency_id : true);

      const conversion: DepositConversion | null = isConverted
        ? {
            selectedCurrencyId: dep.selected_currency_id ?? '',
            selectedCurrencyAmount: money(Math.round(dep.selected_currency_amount ?? dep.amount)),
            currencyId: dep.currency_id ?? '',
            rate: dep.exchange_rate ?? null,
            rateSource: dep.exchange_rate_source ?? null,
            date: dep.exchange_rate_date ? new Date(dep.exchange_rate_date) : null,
          }
        : null;

      return {
        kind: 'deposit',
        date,
        deposit: {
          id: dep.record_id ?? tx.record_id ?? '',
          description: dep.description ?? '',
          fromId: dep.deposit_from ?? '',
          fromName: resolve.name(dep.deposit_from ?? ''),
          amount: money(Math.round(dep.amount)),
          currencyId: dep.currency_id ?? null,
          currencySymbol: dep.currency_symbol ?? null,
          date,
          createdByName: resolve.name(dep.created_by_user_id ?? ''),
          createdAt: dep.created_at ? new Date(dep.created_at) : null,
          conversion,
        },
      };
    }

    case 'WALLET_CLOSE_TRANSFER': {
      const w = tx.wallet_balance_closure;
      if (!w) return null;
      return {
        kind: 'walletClosure',
        date,
        walletClosure: {
          id: w.record_id ?? tx.record_id ?? '',
          description: w.description ?? '',
          toId: w.transfer_to_user_id ?? '',
          toName: resolve.name(w.transfer_to_user_id ?? ''),
          createdById: w.created_by_user_id ?? '',
          createdByName: w.created_by_user_id ? resolve.name(w.created_by_user_id) : '',
          isFinalTransfer: w.is_final_transfer ?? false,
          amount: money(Math.round(w.amount)),
          currencySymbol: null,
          createdAt: w.created_at
            ? new Date(w.created_at)
            : tx.created_at
            ? new Date(tx.created_at)
            : null,
        },
      };
    }

    default:
      return null;
  }
}

/** Domain → wire shape for create. */
export function toCreateExpenseBody(input: NewExpense): Record<string, unknown> {
  return {
    group_id: input.groupId,
    description: input.description,
    amount_minor: input.amount,
    currency: input.currency,
    paid_by: input.paidByUserId,
  };
}

/** Last dotted segment of a file name (its extension), lower-cased; '' when none/no file. */
function fileExtension(file: File | null | undefined): string {
  if (!file) return '';
  const parts = file.name.split('.');
  return parts.length > 1 ? (parts.pop() ?? '').toLowerCase() : '';
}

/** Local date → `yyyy-MM-dd` (the `pending-expense` date format; no time, no timezone shift). */
function toApiDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * `ExpenseLocation` → the wire's flat `location_*` fields. `latFallback`/`countryFallback` match
 * each endpoint's own "no location" defaults (pending-expense sends '', the group endpoint sends
 * '0'/'null') so omitting a location keeps existing (tested) behavior unchanged.
 */
function toLocationFields(
  location: ExpenseLocation | null | undefined,
  latFallback: string,
  countryFallback: string,
): Record<string, string> {
  if (!location) {
    return {
      location: '',
      location_lat: latFallback,
      location_long: latFallback,
      location_address1: '',
      location_address2: '',
      location_city: '',
      location_state: '',
      location_zip: '',
      location_country: countryFallback,
    };
  }
  return {
    location: location.formattedAddress,
    location_lat: String(location.lat),
    location_long: String(location.long),
    location_address1: location.address1,
    location_address2: location.address2,
    location_city: location.city,
    location_state: location.state,
    location_zip: location.zip,
    location_country: location.country,
  };
}

/**
 * Domain `SaveBillInput` → the `pending-expense` multipart field map. ALL values are stringified
 * (form-data carries no types) and booleans become `"true"`/`"false"` — matching the Flutter
 * `QuickAddExpenseRequestModel.toJson()`. `amount` is sent as a MAJOR-unit string. Deferred features
 * (paid_by / split_configs / items / breakdown / location / file binary) are intentionally omitted;
 * the backend defaults them. `expense_type_id` is only sent when a category is chosen.
 */
export function toPendingExpenseFields(input: SaveBillInput): Record<string, string> {
  const hasGroup = Boolean(input.groupId);
  const fields: Record<string, string> = {
    description: input.description.trim(),
    amount: input.amount.trim(),
    payment_mode: 'Cash',
    date: toApiDate(input.date),
    // The mobile always SENDS these keys (empty when no location picked), so we mirror that for
    // parity (a strict backend schema may expect them present).
    ...toLocationFields(input.location, '', ''),
    context_type: hasGroup ? 'group' : '',
    context_id: input.groupId ?? '',
    currency_id: input.currencyId,
    is_ai_scanned: String(input.isAiScanned ?? false),
    // Neither a kept URL nor a freshly-picked file ⇒ tell the backend to drop any existing file
    // (mirrors mobile `remove_file = receiptUrl=="" && receiptFile==null`).
    remove_file: String(!input.receiptUrl && !input.receiptFile),
    receipt_url: input.receiptUrl ?? '',
    // Extension of the newly-picked file (the binary itself is uploaded separately after save).
    file_extension: fileExtension(input.receiptFile),
    is_private: String(input.isPrivate),
    notes: input.note.trim(),
    save_preferred_currency: String(Boolean(input.savePreferredCurrency)),
    is_currency_converted: String(Boolean(input.conversion)),
  };
  if (input.expenseTypeId) fields.expense_type_id = input.expenseTypeId;

  // Conversion (bill currency ≠ group home currency) — top-level FX fields, only when converted.
  // `selected_currency_amount` is the converted TOTAL in the group currency (major, 2dp); the
  // reconciliation target for Σ group_paid_amount / Σ group_expense_amount (§9, §10).
  const conv = input.conversion;
  if (conv) {
    fields.exchange_rate = String(conv.rate);
    fields.exchange_rate_source = conv.source;
    fields.exchange_rate_date = conv.date;
    fields.selected_currency_amount = fromMinorUnit(conv.convertedTotal);
    fields.selected_currency_id = conv.groupCurrencyId;
  }

  // Breakdown / itemization — `items[]` amounts are INTEGER minor units (not decimal strings). The
  // per-user `item_assignments[]` are intentionally absent (no assignment UI yet — see the type doc).
  // `type` is always "Item" (capitalized) — the server rejects lowercase; the `sing` sign field
  // already differentiates regular items (+) from discounts/charges (-), matching the mobile payload.
  const breakdown = input.breakdownItems ?? [];
  if (breakdown.length > 0) {
    fields.is_breakdown_added = 'true';
    breakdown.forEach((it, i) => {
      fields[`items[${i}][name]`] = it.name;
      fields[`items[${i}][amount]`] = String(it.amountMinor);
      fields[`items[${i}][type]`] = 'Item';
      fields[`items[${i}][sing]`] = it.sign; // wire key is `sing` (sic — matches the backend)
    });
  }

  // Recurrence (Repeat) — bracketed `recurring[...]` keys, emitted only when a rule is set (§16.7).
  // `frequency` is capitalized; dates are local `yyyy-MM-dd`; `end_date` only for the `date` end type;
  // a null start back-fills today for a daily rule (else it's omitted for the server to anchor).
  const rec = input.recurrence;
  if (rec) {
    fields['recurring[frequency]'] = rec.frequency.charAt(0).toUpperCase() + rec.frequency.slice(1);
    fields['recurring[interval]'] = String(rec.interval);
    const startYmd = rec.startDate
      ? toApiDate(rec.startDate)
      : rec.frequency === 'daily'
      ? toApiDate(new Date())
      : '';
    if (startYmd) fields['recurring[start_date]'] = startYmd;
    fields['recurring[end_type]'] = rec.endType;
    if (rec.endType === 'date' && rec.endDate) {
      fields['recurring[end_date]'] = toApiDate(rec.endDate);
    }
    fields['recurring[reminder_days_before]'] = String(rec.reminderDaysBefore);
  }

  // paid_by[] / split_configs[] — bracketed, stringified, amounts as MAJOR-unit decimals (§10).
  // group_paid_amount / group_expense_amount (the group-currency leg) are added only when converted.
  (input.paidBy ?? []).forEach((p, i) => {
    fields[`paid_by[${i}][id]`] = p.userId;
    fields[`paid_by[${i}][type]`] = p.type;
    fields[`paid_by[${i}][split_enabled]`] = String(p.splitEnabled);
    fields[`paid_by[${i}][paid_amount]`] = fromMinorUnit(p.paidAmount);
    if (p.groupPaidAmount !== undefined) {
      fields[`paid_by[${i}][group_paid_amount]`] = fromMinorUnit(p.groupPaidAmount);
    }
    // Multi-payer paid-by method + raw value — sent only when a method was actually chosen in
    // the chained popup, so the plain equal/single-payer payload stays byte-identical.
    if (p.splitType !== undefined) fields[`paid_by[${i}][split_type]`] = p.splitType;
    if (p.percentage !== undefined) fields[`paid_by[${i}][percentage]`] = String(p.percentage);
    if (p.shares !== undefined) fields[`paid_by[${i}][shares]`] = String(p.shares);
    if (p.amount !== undefined) fields[`paid_by[${i}][amount]`] = String(p.amount);
  });
  (input.splitConfigs ?? []).forEach((s, i) => {
    fields[`split_configs[${i}][user_id]`] = s.userId;
    fields[`split_configs[${i}][expense_amount]`] = fromMinorUnit(s.expenseAmount);
    fields[`split_configs[${i}][role]`] = s.role;
    fields[`split_configs[${i}][split_enabled]`] = String(s.splitEnabled);
    fields[`split_configs[${i}][split_type]`] = s.splitType;
    if (s.amount !== undefined) fields[`split_configs[${i}][amount]`] = String(s.amount);
    if (s.percentage !== undefined)
      fields[`split_configs[${i}][percentage]`] = String(s.percentage);
    if (s.shares !== undefined) fields[`split_configs[${i}][shares]`] = String(s.shares);
    if (s.groupExpenseAmount !== undefined) {
      fields[`split_configs[${i}][group_expense_amount]`] = fromMinorUnit(s.groupExpenseAmount);
    }
  });
  // Top-level `split_type` — the group ledger echoes THIS back, and the edit form reads it to pick the
  // Split tab. Without it a split-by-amount/percentage/shares expense reopens as "Evenly". Mirrors the
  // group-bill builder: take the split's type, defaulting to Equal.
  if (input.splitConfigs && input.splitConfigs.length > 0) {
    fields.split_type = input.splitConfigs[0]?.splitType ?? 'Equal';
  }

  return fields;
}

/**
 * Serialize a RECURRING expense for `POST /api/v1/expense/{groupId}` (the group-bill create
 * endpoint). Its multipart shape DIFFERS from pending-expense — matching the mobile payload:
 *  • `currency_amount` (not `amount`), an ISO `date`, `group_id` + `user_id` + `is_draft`,
 *  • `payments[]` (not `paid_by[]`) with `paid_by_user_id` + split_type/amount/shares/percentage,
 *  • `splits[]` (not `split_configs[]`) with `value`, a top-level `split_type`,
 *  • `recurring[end_date]` sent as the literal string "null" when there's no end date,
 *  • several `"null"`-string / default fields the endpoint expects.
 */
export function toGroupExpenseFields(input: SaveBillInput): Record<string, string> {
  const paidBy = input.paidBy ?? [];
  const splitConfigs = input.splitConfigs ?? [];
  const splitType = splitConfigs[0]?.splitType ?? input.storedSplitType ?? 'Equal';
  const breakdown = input.breakdownItems ?? [];
  const fields: Record<string, string> = {
    group_id: input.groupId ?? '',
    user_id: input.userId ?? '',
    is_draft: 'false',
    currency_amount: input.amount.trim(),
    description: input.description.trim(),
    currency_id: input.currencyId,
    date: `${toApiDate(input.date)}T00:00:00.000Z`,
    notes: input.note.trim(),
    is_private: String(input.isPrivate),
    // No-location fallbacks are EMPTY strings — the same representation the pending-expense
    // create (and mobile) store for "no location". They used to be '0'/'null' literals here,
    // which (a) wrote a fake (0,0) coordinate and a literal "null" country into the document,
    // and (b) made every ledger EDIT of a location-less expense differ from the stored values,
    // so group activity's "View changes" logged phantom LOCATION diffs the user never made.
    ...toLocationFields(input.location, '', ''),
    is_ai_scanned: String(input.isAiScanned ?? false),
    is_breakdown_added: String(breakdown.length > 0),
    is_duplicate_receipt: 'false',
    receipt_number: 'null',
    metadata: 'null',
    remove_file: String(!input.receiptUrl && !input.receiptFile),
    created_at: 'null',
    updated_at: 'null',
    file_url: 'null',
    file_extension: fileExtension(input.receiptFile),
    save_preferred_currency: String(Boolean(input.savePreferredCurrency)),
    is_exchanged: 'false',
    is_currency_converted: String(Boolean(input.conversion)),
    attachment: 'null',
  };
  if (input.expenseTypeId) fields.expense_type_id = input.expenseTypeId;

  // Top-level `split_type`: real split configs win; with none (a PRIVATE expense has no split UI),
  // an EDIT re-sends the expense's STORED value verbatim — or keeps the key ABSENT when the
  // expense never stored one, exactly what its create sent. Unconditionally sending 'Equal' here
  // made every description-only edit of a private expense log a phantom SPLIT change in group
  // activity. Creates (no id) keep the historical 'Equal' default.
  if (splitConfigs.length > 0 || input.storedSplitType || !input.id) {
    fields.split_type = splitType;
  }

  // splits[] — expense_amount (major), user_id, split_enabled, value (raw per-mode input, else 0).
  splitConfigs.forEach((s, i) => {
    fields[`splits[${i}][expense_amount]`] = fromMinorUnit(s.expenseAmount);
    fields[`splits[${i}][user_id]`] = s.userId;
    fields[`splits[${i}][split_enabled]`] = String(s.splitEnabled);
    fields[`splits[${i}][value]`] = String(s.percentage ?? s.shares ?? s.amount ?? 0);
    // Group-currency mirror of the share — present only on a converted bill.
    if (s.groupExpenseAmount !== undefined) {
      fields[`splits[${i}][group_expense_amount]`] = fromMinorUnit(s.groupExpenseAmount);
    }
  });

  // payments[] — type, paid_by_user_id / paid_by_wallet_id, paid_amount (major), split_enabled +
  // defaults.
  paidBy.forEach((p, i) => {
    fields[`payments[${i}][type]`] = p.type;
    // A WALLET leg's id is the wallet ACCOUNT id, and this endpoint wants it on
    // `paid_by_wallet_id` — sending it as `paid_by_user_id` fails validation with
    // "WALLET payment requires paid_by_wallet_id". Member legs keep `paid_by_user_id`.
    if (p.type === 'wallet') {
      fields[`payments[${i}][paid_by_wallet_id]`] = p.userId;
    } else {
      fields[`payments[${i}][paid_by_user_id]`] = p.userId;
    }
    fields[`payments[${i}][paid_amount]`] = fromMinorUnit(p.paidAmount);
    fields[`payments[${i}][split_enabled]`] = String(p.splitEnabled);
    // The paid-by METHOD + its raw value per leg (mobile parity): a percentage-paid bill sends
    // split_type "Percentage" + each payer's percentage — these used to be hardcoded to the
    // evenly defaults ('0'/'1'/'0'), so the payload never named how the payers actually split.
    fields[`payments[${i}][split_type]`] = p.splitType ?? splitType;
    fields[`payments[${i}][amount]`] = String(p.amount ?? 0);
    fields[`payments[${i}][shares]`] = String(p.shares ?? 1);
    fields[`payments[${i}][percentage]`] = String(p.percentage ?? 0);
    // Group-currency mirror of the paid leg — present only on a converted bill.
    if (p.groupPaidAmount !== undefined) {
      fields[`payments[${i}][group_paid_amount]`] = fromMinorUnit(p.groupPaidAmount);
    }
  });

  // recurring[...] — `end_date` is the literal string "null" unless it's an on-date rule.
  const rec = input.recurrence;
  if (rec) {
    fields['recurring[frequency]'] = rec.frequency.charAt(0).toUpperCase() + rec.frequency.slice(1);
    fields['recurring[interval]'] = String(rec.interval);
    fields['recurring[start_date]'] = toApiDate(rec.startDate ?? input.date);
    fields['recurring[end_type]'] = rec.endType;
    fields['recurring[end_date]'] =
      rec.endType === 'date' && rec.endDate ? toApiDate(rec.endDate) : 'null';
    fields['recurring[reminder_days_before]'] = String(rec.reminderDaysBefore);
  }

  // Conversion top-level fields (only when converted). UNLIKE pending-expense, this endpoint
  // takes the expense DOCUMENT's own field names — `group_currency_id` / `group_currency_amount`,
  // not `selected_currency_id` / `selected_currency_amount` (those are pending-only; the group
  // validator strips unknown keys, and a missing `group_currency_id` falls back to `currency_id`,
  // silently re-denominating a converted expense in its own currency on every edit).
  const conv = input.conversion;
  if (conv) {
    fields.exchange_rate = String(conv.rate);
    fields.exchange_rate_source = conv.source;
    fields.exchange_rate_date = conv.date;
    fields.group_currency_amount = fromMinorUnit(conv.convertedTotal);
    fields.group_currency_id = conv.groupCurrencyId;
  }

  // Breakdown items[] (only when present) — integer minor amounts, `sing` sign.
  // `type` is always "Item" (capitalized) — server rejects lowercase; sign field differentiates.
  breakdown.forEach((it, i) => {
    fields[`items[${i}][name]`] = it.name;
    fields[`items[${i}][amount]`] = String(it.amountMinor);
    fields[`items[${i}][type]`] = 'Item';
    fields[`items[${i}][sing]`] = it.sign;
  });

  return fields;
}

/** `pending-expense` save response → the new id + profile-popup flag. */
export function toSavedBill(res: PendingExpenseSaveResponse): SavedBill {
  const expense = res.data?.expense;
  return {
    id: expense?._id ?? expense?.id ?? '',
    showProfilePopup: res.data?.show_profile_popup ?? false,
  };
}
